import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import '../../board/board-search-filters/BoardSearchFilters.css';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import { labelRegBugOption } from '../../process-config/components/condicoesIntervShared';

/** Serviço obrigatório do novo ciclo. A lista é o mesmo seletor simples do board. */
export function CheckoutCycleServiceField({ numeroOS, onSaved }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const layout = useEcFilterPopoverLayout(anchorRef, open, 240, 280);

  useEffect(() => {
    let cancel = false;
    const token = localStorage.getItem('authToken');
    fetch(`${getApiBaseUrl()}/api/checkout-view/ciclo-servicos`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((json) => { if (!cancel) setItems(Array.isArray(json.data) ? json.data : []); })
      .catch(() => { if (!cancel) setItems([]); });
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-cycle-servico]');
      if (pop?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const options = useMemo(() => {
    const all = items.map((b) => ({ cod: String(b.cod_bug || '').trim(), label: labelRegBugOption(b) })).filter((o) => o.cod);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) => o.label.toLowerCase().includes(q) || o.cod.toLowerCase().includes(q));
  }, [items, search]);

  const current = items.find((b) => String(b.cod_bug || '').trim() === sel);
  const label = current ? labelRegBugOption(current) : (sel || 'Selecione o serviço');
  const anchorBox = open && anchorRef.current ? anchorRef.current.getBoundingClientRect() : null;
  const popStyle = layout
    ? {
        ...buildEcFilterPopoverStyle(layout),
        zIndex: 10050,
        width: anchorBox?.width || layout.width,
        left: anchorBox?.left ?? layout.left,
      }
    : null;

  const choose = async (cod) => {
    if (!cod || saving) return;
    const prev = sel;
    setSaving(true);
    setError('');
    onSaved('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${getApiBaseUrl()}/api/checkout-view/ciclo-servico`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ numeroOS, codigoDefeito: cod }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Não foi possível gravar o serviço.');
      setSel(cod);
      setSearch('');
      setOpen(false);
      onSaved(cod);
    } catch (e) {
      setError(e.message || 'Não foi possível gravar o serviço.');
      onSaved(prev);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="checkout-view-cycle-service">
      <span className="checkout-view-cycle-service-label">Serviço do novo ciclo</span>
      <div className={`tm-combo${open ? ' is-open' : ''}`} ref={anchorRef}>
        <button
          type="button"
          className={`checkout-view-cycle-service-trigger${sel ? '' : ' is-empty'}`}
          disabled={saving}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Selecionar serviço do novo ciclo"
          onClick={() => setOpen((v) => !v)}
        >
          <span>{saving ? 'Salvando…' : label}</span>
          <ChevronDown size={14} strokeWidth={1.75} aria-hidden />
        </button>
      </div>
      {error ? <p className="checkout-view-cycle-service-error">{error}</p> : null}
      {open && popStyle ? ReactDOM.createPortal(
        <div
          data-cycle-servico=""
          className={EC_FILTER_POPOVER_CLASS}
          style={popStyle}
          role="dialog"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <BoardSearchSimplePickerPanel
            searchPlaceholder="Buscar serviço..."
            searchTerm={search}
            onSearchChange={setSearch}
            items={options}
            getKey={(item) => item.cod}
            getLabel={(item) => item.label}
            isSelected={(item) => item.cod === sel}
            onToggle={(item) => { choose(item.cod); }}
            emptyMessage="Nenhum serviço"
            listAriaLabel="Serviço do novo ciclo"
          />
        </div>,
        document.body
      ) : null}
    </div>
  );
}
